import { Box, Button, CircularProgress, styled, Typography } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import React, { useEffect, useRef, useState } from 'react'
import Webcam from 'react-webcam'
import OvalOverlay from './styled/OvalOverlay'
import { useUpdateKycMutation, useValidateSelfieMutation } from '~/app/store/services'
import { visionAxios } from '~/utils'
import { cancelNarration, speak, t } from '~/utils/narration'

const StyledImageBox = styled(Box)(({ theme }) => ({
  position: 'fixed',
  zIndex: 1,
  top: 0,
  left: 0,
  right: 0,
  bottom: '32%',
  height: '68%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#232323',
  padding: '48px 36px 60px 28px',
}))

const inputResolution = { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }

const KycSelfieCapture = ({ onContinue, id }: any) => {

  const webcamRef = useRef<Webcam>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isCapturing, setCapturing] = useState<boolean>(false)
  const [screenshot, setScreenshot] = useState<string | null | undefined>(null)
  const [showScreenshot, setShowScreenshot] = useState(false)
  const [retakeAllowed, setRetakeAllowed] = useState(false)
  const [updateKyc] = useUpdateKycMutation()
  const [validateSelfie] = useValidateSelfieMutation()

  const [webcamKey, setWebcamKey] = useState(0)
  const [cameraError, setCameraError] = useState(false)
  const camAttempts = useRef(0)
  const onCameraError = (err: string | DOMException) => {
    console.error('camera failed to start', err)
    if (camAttempts.current < 4) {
      camAttempts.current += 1
      setTimeout(() => setWebcamKey((k) => k + 1), 700)
    } else setCameraError(true)
  }
  const retryCamera = () => {
    camAttempts.current = 0
    setCameraError(false)
    setWebcamKey((k) => k + 1)
  }
  
  const streamRef = useRef<MediaStream | null>(null)
  const enableAutoFocus = async () => {
    try {
      const track = streamRef.current?.getVideoTracks?.()[0]
      if (!track) return
      const caps = (track.getCapabilities?.() ?? {}) as any
      if (Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
        await track.applyConstraints({ advanced: [{ focusMode: 'continuous' } as any] })
      }
    } catch (err) {
      console.warn('continuous focus not available on this device', err)
    }
  }

  useEffect(() => {
    speak({
      en: 'Next, we will capture a photo of you. Make sure your face is clearly visible.',
      hi: 'अब हम आपकी एक तस्वीर लेंगे। कृपया सुनिश्चित करें कि आपका चेहरा स्पष्ट रूप से दिखाई दे।',
    })
    return () => cancelNarration()
  }, [])

  // const checkBlurriness = async (image: string | null | undefined) => {
  //   if (image) {
  //     const payload = { input_image: image.split(',')[1] }
  //     const response = await visionAxios.post(`/docblur`, payload)
  //     const data = response.data
  //     console.log('image-blurr', data)
  //     return data
  //   }
  // }

  const handleCapture = async () => {
    try {
      const image = webcamRef.current?.getScreenshot()
      setScreenshot(image)
      if (!image) throw new Error('Failed to capture image')

      setShowScreenshot(true)
      setRetakeAllowed(true)
    } catch (error) {
      enqueueSnackbar(t({ en: error.message, hi: 'तस्वीर लेने में विफल। कृपया पुनः प्रयास करें।' }), {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      })
    }
  }

  const handleRetakeButtonClick = () => {
    setShowScreenshot(false)
    setRetakeAllowed(false)
  }

  const handleContinueWithScreenshot = async () => {
    try {
      if (!screenshot) throw new Error('Failed to capture image')
      setIsLoading(true)

      const retake = (msg: string) => {
        enqueueSnackbar(msg, {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        })
        speak(msg)
        setScreenshot(null)
        setShowScreenshot(false)
        setRetakeAllowed(false)
      }

      // const checkBlur = await checkBlurriness(screenshot)
      // if (checkBlur && checkBlur.blurry === 'Yes') {
      //   retake('Image is blurry. Please try again.')
      //   return
      // }

      const face = await validateSelfie({ id, image: screenshot }).unwrap()
      if (!face.valid) {
        const messages: Record<string, string> = {
          no_face: t({
            en: 'No face detected. Please retake with your face clearly visible.',
            hi: 'कोई चेहरा नहीं मिला। कृपया अपना चेहरा स्पष्ट दिखाकर फिर से फ़ोटो लें।',
          }),
          multiple_faces: t({
            en: 'More than one person is visible. Make sure only you are in the frame.',
            hi: 'एक से अधिक व्यक्ति दिख रहे हैं। सुनिश्चित करें कि फ़्रेम में केवल आप ही हैं।',
          }),
          sunglasses: t({
            en: 'Please remove your sunglasses and retake the photo.',
            hi: 'कृपया अपना धूप का चश्मा हटाएँ और फिर से फ़ोटो लें।',
          }),
          eyeglasses: t({
            en: 'Please remove your glasses and retake the photo.',
            hi: 'कृपया अपना चश्मा हटाएँ और फिर से फ़ोटो लें।',
          }),
          face_covered: t({
            en: 'Your face is partly covered. Remove any mask or covering and retake.',
            hi: 'आपका चेहरा आंशिक रूप से ढका है। कोई मास्क या आवरण हटाएँ और फिर से फ़ोटो लें।',
          }),
          eyes_closed: t({
            en: 'Please keep your eyes open and retake the photo.',
            hi: 'कृपया अपनी आँखें खुली रखें और फिर से फ़ोटो लें।',
          }),
          blurry: t({
            en: 'Your face looks blurry. Hold steady and retake in focus.',
            hi: 'आपका चेहरा धुंधला दिख रहा है। स्थिर रहें और फ़ोकस में फिर से फ़ोटो लें।',
          }),
        }
        retake(
          messages[face.reason as string] ||
            t({
              en: 'Please retake with your face clearly visible.',
              hi: 'कृपया अपना चेहरा स्पष्ट दिखाकर फिर से फ़ोटो लें।',
            })
        )
        return
      }

      await updateKyc({ image: screenshot, id, status: 'video_uploaded' })
      onContinue()
    } catch (error) {
      console.log('Failed to capture image:', error.message)
      enqueueSnackbar(t({ en: 'Something went wrong! Please try again.', hi: 'कुछ गड़बड़ हो गई! कृपया पुनः प्रयास करें।' }), {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      })
    } finally {
      setIsLoading(false)
      setCapturing(false)
    }
  }

  return (
    <>
      {showScreenshot && screenshot ? (
        <StyledImageBox>
          <img
            src={screenshot}
            alt="Captured Selfie"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              border: '6px solid #FFF',
              borderRadius: 4,
            }}
          />
        </StyledImageBox>
      ) : (
        <Box position="fixed" zIndex={1} bgcolor={'black'} top="0%" left={0} right={0} height="68%" onClick={enableAutoFocus}>
          <Webcam
            key={webcamKey}
            ref={webcamRef}
            videoConstraints={inputResolution}
            mirrored={true}
            forceScreenshotSourceSize
            screenshotQuality={0.92}
            imageSmoothing={false}
            screenshotFormat="image/jpeg"
            onUserMedia={(stream) => {
              camAttempts.current = 0
              setCameraError(false)
              streamRef.current = stream
              enableAutoFocus()
            }}
            onUserMediaError={onCameraError}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <OvalOverlay />
          {cameraError && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                px: 3,
                textAlign: 'center',
                color: '#fff',
              }}
            >
              <Typography variant="subtitle1">
                {t({
                  en: 'Couldn’t start the camera. Make sure no other app is using it, then try again.',
                  hi: 'कैमरा शुरू नहीं हो सका। सुनिश्चित करें कि कोई अन्य ऐप इसका उपयोग नहीं कर रहा है, फिर पुनः प्रयास करें।',
                })}
              </Typography>
              <Button variant="contained" onClick={retryCamera}>
                {t({ en: 'Retry camera', hi: 'कैमरा पुनः आज़माएँ' })}
              </Button>
            </Box>
          )}
        </Box>
      )}
      <Box
        display="flex"
        flexDirection="column"
        position="fixed"
        bottom="0%"
        height="35%"
        width="100%"
        left={0}
        alignItems="center"
        zIndex={2}
        borderRadius="30px 30px 0 0"
        padding="16px 24px"
        bgcolor="#FFF"
      >
        {isLoading ? (
          <>
            <Box sx={{ width: '100%' }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {isCapturing
                  ? t({ en: 'Uploading photo, please wait...', hi: 'फ़ोटो अपलोड हो रही है, कृपया प्रतीक्षा करें...' })
                  : t({ en: 'Analysing your photo, please wait...', hi: 'आपकी फ़ोटो का विश्लेषण हो रहा है, कृपया प्रतीक्षा करें...' })}
              </Typography>
              <Typography variant="subtitle1">
                {t({
                  en: 'Please avoid hitting the back button or refreshing the page during this process.',
                  hi: 'इस प्रक्रिया के दौरान कृपया बैक बटन न दबाएँ या पेज रीफ़्रेश न करें।',
                })}
              </Typography>

              <CircularProgress sx={{ marginTop: 6 }} />
            </Box>
          </>
        ) : (
          <>
            {retakeAllowed ? (
              <>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t({ en: 'Would you like to continue with this photo?', hi: 'क्या आप इस फ़ोटो के साथ आगे बढ़ना चाहेंगे?' })}
                </Typography>
                <Box flex={1} />
                <Box
                  position="absolute"
                  bottom={0}
                  width="100%"
                  padding={2}
                  borderTop="1px solid #E0E0E0"
                >
                  <Button
                    size="large"
                    variant="outlined"
                    fullWidth
                    sx={{ py: 1.5, mb: 1 }}
                    disabled={isLoading}
                    onClick={handleRetakeButtonClick}
                  >
                    {t({ en: 'Re-capture', hi: 'फिर से लें' })}
                  </Button>
                  <Button
                    size="large"
                    fullWidth
                    sx={{ py: 1.5 }}
                    disabled={isLoading}
                    onClick={handleContinueWithScreenshot}
                  >
                    {t({ en: 'Yes, continue', hi: 'हाँ, आगे बढ़ें' })}
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ px: 2, py: 2 }}>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {t({ en: 'Next, we will capture a photo of you', hi: 'अब हम आपकी एक फ़ोटो लेंगे' })}
                  </Typography>
                  <Typography variant="subtitle1">
                    {t({
                      en: 'Make sure your face is clearly visible and evenly illuminated, with no mask or glass.',
                      hi: 'सुनिश्चित करें कि आपका चेहरा स्पष्ट दिखे और उस पर एक समान रोशनी हो, कोई मास्क या चश्मा न हो।',
                    })}
                  </Typography>
                </Box>

                <Box
                  position="absolute"
                  bottom={0}
                  width="100%"
                  padding={2}
                  borderTop="1px solid #E0E0E0"
                >
                  <Button size="large" fullWidth sx={{ py: 1.5 }} disabled={isLoading} onClick={handleCapture}>
                    {t({ en: 'Capture your photo', hi: 'अपनी फ़ोटो लें' })}
                  </Button>
                </Box>
              </>
           )}
          </>
        )}
      </Box>
    </>
  )
}

export default KycSelfieCapture